import {useMutation} from '@tanstack/react-query';
import {TypeLocalNameEnum} from 'domain/usecases';
import {EntryPointCodeEnum} from 'presentation/constants/entryPointCodeEnum';
import {
	MANUAL_DISTANCE_ORIGIN,
	MAX_DISTANCE_FILTER_UNITS,
} from 'presentation/modules/booking/shared/constants/unitsInfo';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {getHealthProviderLocationPreview} from '../../services/getHealthProviderLocationPreview';

import {FeaturesEnum, QueryParamsEnum} from 'presentation/constants';
import {useQueryParams} from 'presentation/pages';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';

export function useGetHealthProviderPreviewMutation() {
	const bookingData = useBookingStore(state => state.bookingData);
	const {
		SEARCH_PARAMS: {distanceOrigin},
		setSearchParams,
	} = useQueryParams();

	const {isEnabled: isUnitsNetworksEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_BOOKING_UNITS_NETWORKS,
	);

	const isManualDistanceSearch =
		isUnitsNetworksEnabled && distanceOrigin === MANUAL_DISTANCE_ORIGIN;

	const {
		isManualLocation,
		coordinates,
		cityName,
		specialtyCode,
		ufCode,
		subSpecialtyCode,
		typeLocalName,
	} = bookingData;

	const isUsingGeolocation = !isManualLocation;

	const params = {
		cityName: isUsingGeolocation ? coordinates : cityName?.toUpperCase(),
		specialtyCode,
		stateCode: isUsingGeolocation ? undefined : ufCode?.toUpperCase(),
		subSpecialtyCode,
		typeLocalName: typeLocalName || TypeLocalNameEnum.CIDADE,
		distance: MAX_DISTANCE_FILTER_UNITS.toString(),
		entryPointCode: Number(EntryPointCodeEnum.V2),
	};

	return useMutation({
		mutationFn: async ({signal}: {signal?: AbortSignal}) =>
			getHealthProviderLocationPreview({signal, ...params}),
		onSuccess: hasUnitsUpToMaxDistance => {
			if (hasUnitsUpToMaxDistance?.Content === false && !isManualDistanceSearch) {
				const searchParams = new URLSearchParams(window.location.search);
				searchParams.set(
					QueryParamsEnum.LOCALITY_DISTANCE,
					MAX_DISTANCE_FILTER_UNITS.toString(),
				);
				setSearchParams(searchParams);
			}

			return hasUnitsUpToMaxDistance.Content;
		},
	});
}
